import PropTypes from "prop-types";
import {
  Container,
  Box,
  Typography,
  Button,
  CircularProgress,
  useMediaQuery,
  Link,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import useSurveyValidation from "../../hooks/useSurveyValidation";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import SurveyTextField from "../pre-chat/SurveyTextField";
import CheckmarkIcon from "../icons/CheckmarkIcon";
import SurveyPillQuestion from "../shared/SurveyPillQuestion";
import SurveyPhoneNumberQuestion from "../shared/SurveyPhoneNumberQuestion";
import {
  getPreSmsChoices,
  TOS_PARAM_MAP,
  UA_PARAM_MAP,
} from "../../preSmsChoices";
import {
  replaceMainSiteLinkHostname,
  serializePreSmsSurvey,
  normalizeLanguage,
  parseBooleanParam,
} from "../../services/Utils";
import surveyLinkStyles from "../../styles/surveyLinkStyles";
import SessionInvalidPage from "../shared/SessionInvalidPage";
import AlreadySubmittedPage from "../shared/AlreadySubmittedPage";
import InternalServerErrorPage from "../shared/InternalServerErrorPage";
import SubmissionErrorBanner from "../shared/SubmissionErrorBanner";
import useRecaptcha from "../../hooks/useRecaptcha";

import axios from "axios";
import axiosRetry from "axios-retry";
import * as Sentry from "@sentry/react";

axiosRetry(axios, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) =>
    axiosRetry.isNetworkError(error) ||
    axiosRetry.isRetryableError(error) ||
    (error.response &&
      error.response.status >= 500 &&
      error.response.status < 600),
});

const SurveyForm = ({ filteredParams = {}, modality }) => {
  const pageLoadAt = useMemo(() => new Date(), []);
  const { interactionId: interactionIdKey } = useParams();
  const [view, setView] = useState("form");
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const { t, i18n } = useTranslation("preSms", "common");
  const { mainConcernChoices } = getPreSmsChoices(t);
  const { executeRecaptcha } = useRecaptcha();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lang = searchParams.get("lang");
  const query = lang === "es" ? "?lang=es" : "";

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm({
    defaultValues: {
      zipCode: "",
      name: "",
      age: "",
      mainConcerns: [],
      mainConcerns_unlisted: "",
      phoneNumber: "",
    },
  });

  const submitPreSms = async (raw) => {
    const submissionEndpoint = import.meta.env
      .VITE_LIFELINE_CHAT_SURVEY_API_URL;
    if (!submissionEndpoint) return;

    let recaptchaToken = "";
    try {
      recaptchaToken = await executeRecaptcha("submit");
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          action: "recaptcha_execution",
          formContext: "survey_submit",
        },
      });
    }

    let cleaned = {};
    for (const [key, value] of Object.entries(raw)) {
      if (!(key.endsWith("_unlisted") && value === undefined)) {
        cleaned[key] = value;
      }
    }
    // Filter out unnecessary fields ending with "_unlisted" if their value is undefined
    cleaned = {
      ...cleaned,
      modality,
      interactionIdKey,
      recaptchaToken,
      locale: normalizeLanguage(i18n.language),
      caseID: filteredParams?.caseID,
      surveyID: filteredParams?.surveyID,
      isTrevor: parseBooleanParam(filteredParams?.trv),
      upsetAmount: UA_PARAM_MAP[filteredParams?.ua],
      thoughtsOfSuicide: TOS_PARAM_MAP[filteredParams?.tos?.toLowerCase()],
      page_load_timestamp:
        cleaned.page_load_timestamp ||
        pageLoadAt.toISOString().slice(0, 19) + "Z",
    };

    const submissionPayload = serializePreSmsSurvey(cleaned);

    try {
      await axios.post(submissionEndpoint, submissionPayload, {
        timeout: 30000,
      });
      navigate(`/${interactionIdKey}/thank-you${query}`);
    } catch (error) {
      const statusCode = error.response?.status;
      if (statusCode === 400) {
        setView("sessionInvalid");
        return;
      }
      if (statusCode === 409) {
        setView("alreadySubmitted");
        return;
      }
      setView("internalServerError");
      Sentry.captureException(error, {
        tags: {
          component: "PreSmsSurveyForm",
          action: "form_submission",
        },
        extra: {
          statusCode,
          responseData: error.response?.data,
        },
        level: "error",
      });
    }
  };

  const validationStatus = useSurveyValidation({
    endpoint: import.meta.env.VITE_LIFELINE_PRESMS_SURVEY_VALIDATE_API_URL,
    componentName: "PreSmsSurveyForm",
    interactionIdKey,
  });

  if (validationStatus === "loading")
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  if (validationStatus === "internalServerError")
    return <InternalServerErrorPage />;
  if (validationStatus === "sessionInvalid") return <SessionInvalidPage />;
  if (validationStatus === "alreadySubmitted") return <AlreadySubmittedPage />;

  const onSubmit = async (data) => {
    await submitPreSms(data);
  };

  if (view === "sessionInvalid") return <SessionInvalidPage />;
  if (view === "alreadySubmitted") return <AlreadySubmittedPage />;

  return (
    <Container
      maxWidth="md"
      disableGutters
      sx={{ bgcolor: theme.palette.white, borderRadius: "10px" }}
    >
      {view === "internalServerError" && <SubmissionErrorBanner />}
      <Box
        sx={{
          width: "100%",
          boxSizing: "border-box",
          maxWidth: {
            xs: "none",
            sm: theme.breakpoints.values.sm - 20,
            lg: theme.breakpoints.values.sm - 24,
          },
          mx: "auto",
          px: { xs: "10px", sm: 0 },
          pt: isDesktop ? 4 : "4px",
          textAlign: "left",
        }}
      >
        <Typography
          component="h1"
          variant="h1"
          sx={{
            fontSize: { xs: "22px", sm: "23px", lg: "24px" },
            fontWeight: 700,
            lineHeight: { xs: 1.25, lg: 1.25 },
            whiteSpace: "normal",
            mb: 0.75,
            textAlign: "center",
          }}
        >
          {t("title")}
        </Typography>
      </Box>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Container
          maxWidth="sm"
          disableGutters
          sx={{
            mt: { xs: 1, lg: 2 },
            pt: isDesktop ? 1 : 0,
            mb: isDesktop ? 13 : 0,
            pb: 1.5,
            px: isDesktop ? 1.5 : "10px",
            bgcolor: "transparent",
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              height: "54px !important",
            },
            "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
              transform: "translate(1.4rem, 1.1rem) scale(1)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: "10px", lg: "8px" },
              "& .MuiFormControl-root": { my: 0 },
            }}
          >
            <SurveyTextField
              label={t("zipCodeQuestion.placeHolder")}
              name="zipCode"
              autoComplete="postal-code"
              title={t("zipCodeQuestion.title")}
              description={t("zipCodeQuestion.infoText")}
              control={control}
              maskFormat="#####"
              inputProps={{ inputMode: "numeric" }}
            />

            <SurveyTextField
              label={t("nameAliasQuestion.placeHolder")}
              name="name"
              autoComplete="name"
              title={t("nameAliasQuestion.title")}
              description={t("nameAliasQuestion.infoText")}
              control={control}
              inputProps={{ maxLength: 50 }}
            />

            <SurveyTextField
              label={t("ageQuestion.placeHolder")}
              name="age"
              title={t("ageQuestion.title")}
              description={t("ageQuestion.infoText")}
              control={control}
              maskFormat="###"
              inputProps={{ inputMode: "numeric" }}
            />
          </Box>

          <Box sx={{ textAlign: "left", mt: 3.5 }}>
            <SurveyPillQuestion
              mainLabelText={t("mainConcernsQuestion.title")}
              subLabelText={t("mainConcernsQuestion.subText")}
              control={control}
              spacingTop={0}
              spacingBottom={0}
              options={mainConcernChoices}
              name="mainConcerns"
            />
          </Box>
          <Box sx={{ textAlign: "left", mt: 3.5, mb: 3 }}>
            <SurveyPhoneNumberQuestion
              control={control}
              name="phoneNumber"
              errors={errors}
              modality="sms"
              showOptionalLabel={false}
            />
          </Box>
          <Button
            type="submit"
            variant="contained"
            disableElevation
            disableRipple
            disabled={isSubmitting}
            sx={{
              mt: 3,
              mb: 2,
              width: 181,
              height: 70,
              borderRadius: 50,
              display: "flex",
              gap: 1,
              alignItems: "center",
              mx: "auto",
              fontFamily: "Public Sans",
              fontWeight: "bold",
              fontSize: 22,
              bgcolor: theme.palette.primary.main,
              textTransform: "none",
              color: theme.palette.white,
              ":hover": {
                bgcolor: theme.palette.secondary.dark,
              },
              ":focus": {
                bgcolor: theme.palette.secondary.dark,
                outline: `1px dashed ${theme.palette.tertiary.main}`,
                outlineOffset: 4,
              },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <>
                <CheckmarkIcon
                  sx={{
                    "& path": { fill: "white" },
                    width: 42,
                    height: 42,
                    mt: "5px",
                    mr: "-4px",
                  }}
                />
                {t("submitButton")}
              </>
            )}
          </Button>
          <Typography
            variant="body1"
            sx={{ textAlign: "center", mb: 1.5, mt: "20px" }}
          >
            {t("startText.termsOfServiceText")}{" "}
            <Link
              href={replaceMainSiteLinkHostname(
                t("startText.termsOfServiceUrl"),
              )}
              underline="always"
              sx={(theme) => surveyLinkStyles(theme)}
              target="_blank"
            >
              {t("startText.termsOfServiceLink")}
            </Link>
          </Typography>
          <Typography variant="body1" sx={{ textAlign: "center", mb: 7 }}>
            {t("startText.whatToExpectPreamble")}
            <Link
              href={replaceMainSiteLinkHostname(t("startText.whatToExpectUrl"))}
              underline="always"
              sx={(theme) => surveyLinkStyles(theme)}
              target="_blank"
              rel="noopener"
            >
              {t("startText.whatToExpectLink")}
            </Link>{" "}
            {t("startText.whatToExpectText")}
          </Typography>
        </Container>
      </Box>
    </Container>
  );
};

SurveyForm.propTypes = {
  modality: PropTypes.string,
  filteredParams: PropTypes.object,
};

export default SurveyForm;
